import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Reader = ({ book, onClose, pageTurn, showProgress }) => {
  const total = book.pages.length;
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const flip = pageTurn === "flip";

  const goNext = useCallback(() => {
    setDirection(1);
    setIndex((i) => Math.min(i + 1, total - 1));
  }, [total]);
  const goPrev = useCallback(() => {
    setDirection(-1);
    setIndex((i) => Math.max(i - 1, 0));
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [goNext, goPrev, onClose]);

  const variants = {
    enter: (d) => (flip ? { opacity: 0, x: d > 0 ? 80 : -80, rotateY: d > 0 ? 10 : -10 } : { opacity: 0 }),
    center: { opacity: 1, x: 0, rotateY: 0 },
    exit: (d) => (flip ? { opacity: 0, x: d > 0 ? -80 : 80, rotateY: d > 0 ? -10 : 10 } : { opacity: 0 }),
  };

  return (
    <div data-testid="reader-overlay" className="fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur-md">
      <div className="flex items-center justify-between gap-4 border-b border-border bg-background/80 px-4 py-3 md:px-8">
        <Button
          variant="ghost"
          size="sm"
          data-testid="reader-close-btn"
          onClick={onClose}
          className="rounded-full transition-colors hover:bg-accent/10"
        >
          <X className="mr-1 h-4 w-4" />
          Close
        </Button>
        <p className="truncate font-display text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          {book.title}
        </p>
        <p data-testid="page-indicator" className="shrink-0 text-xs tabular-nums text-muted-foreground">
          Page {index + 1} of {total}
        </p>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-14 py-6 md:px-24" style={{ perspective: 1200 }}>
        <AnimatePresence mode="wait" custom={direction}>
          <motion.img
            key={index}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: "easeOut" }}
            src={`${BACKEND_URL}${book.pages[index]}`}
            alt={`${book.title} — page ${index + 1}`}
            draggable={false}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.12}
            onDragEnd={(e, info) => {
              if (info.offset.x < -60) goNext();
              else if (info.offset.x > 60) goPrev();
            }}
            className="max-h-full w-auto max-w-full cursor-grab rounded-md border border-border object-contain shadow-[0_24px_60px_rgba(0,0,0,0.45)] active:cursor-grabbing"
            data-testid="reader-page-image"
          />
        </AnimatePresence>

        <Button
          variant="outline"
          size="icon"
          data-testid="reader-prev-btn"
          onClick={goPrev}
          disabled={index === 0}
          aria-label="Previous page"
          className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full transition-transform duration-200 hover:-translate-x-0.5 disabled:opacity-30 md:left-6"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          data-testid="reader-next-btn"
          onClick={goNext}
          disabled={index === total - 1}
          aria-label="Next page"
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full transition-transform duration-200 hover:translate-x-0.5 disabled:opacity-30 md:right-6"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {showProgress && (
        <div data-testid="reader-progress" className="h-1 w-full bg-muted">
          <div
            className="h-full bg-primary transition-[width] duration-300"
            style={{ width: `${((index + 1) / total) * 100}%` }}
          />
        </div>
      )}
    </div>
  );
};
